/**
 * @file OrderIssueDocumentDialog.tsx
 * @description Счёт или УПД прямо из карточки заказа (phase19.24 этап 1,
 * шаг 5; находка повторного ревью 2026-09-21).
 *
 * До этого документ по заказу заводили только со сделки: у заказа с сайта
 * сделки может не быть вовсе, и узел «Счёт» в «Пути заказа» вечно оставался
 * будущим с подсказкой «создастся, когда вы выставите счёт» — а выставить его
 * из заказа было негде.
 *
 * Основание передаём полным набором связей (`orderId`, `customerId`,
 * `legalEntityId`, `dealId`): по `orderId` документ попадает во вкладку
 * «Оплаты и документы» этого заказа, остальные связи сервер использует для
 * реквизитов. Сумму и позиции считает сервер по заказу — клиент их не
 * выдумывает.
 *
 * Черновик создаётся и сразу открывается: выпуск, PDF и подпись живут на
 * странице документа, дублировать их здесь нельзя — разъедутся.
 */

import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { SelectField } from "../ui/SelectField";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  DOCUMENT_KIND_LABEL,
  createDocument,
  type DocumentKind,
} from "../../lib/api-crm";
import type { OrderDto } from "../../lib/api-orders";

/**
 * Виды, которые выставляют по заказу. Реестр, а не список в разметке: узлы
 * «Пути заказа» знают ровно эти два вида (`INVOICE`, `UPD`), и третий вид
 * добавляется записью.
 */
const ORDER_DOCUMENT_KINDS: DocumentKind[] = ["INVOICE", "UPD_STATUS_2", "UPD_STATUS_1"];

interface Props {
  order: OrderDto;
  open: boolean;
  onClose: () => void;
  /** Перечитать список документов заказа после создания черновика. */
  onCreated?: () => void;
  /** Слаг магазина — переход на созданный документ. */
  slug: string | undefined;
}

export function OrderIssueDocumentDialog({ order, open, onClose, onCreated, slug }: Props) {
  const navigate = useNavigate();
  const notify = useNotify();
  const [kind, setKind] = useState<DocumentKind>("INVOICE");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(): Promise<void> {
    setError(null);
    setSaving(true);
    try {
      const doc = await createDocument({
        kind,
        orderId: order.id,
        customerId: order.customerId,
        legalEntityId: order.legalEntityId ?? undefined,
        dealId: order.dealId ?? undefined,
      });
      notify.success("Черновик документа создан");
      onCreated?.();
      onClose();
      if (slug) navigate(`/shop/${slug}/crm/documents/${doc.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось создать документ",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* На телефоне диалог во весь экран — правило mobile-first DS. */}
      <DialogContent className="max-w-md" fullScreen>
        <DialogHeader>
          <DialogTitle>Документ по заказу {order.number}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? <Alert severity="error">{error}</Alert> : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="order-doc-kind">Тип документа</Label>
            <SelectField
              id="order-doc-kind"
              ariaLabel="Тип документа"
              value={kind}
              onChange={(next) => setKind(next as DocumentKind)}
              options={ORDER_DOCUMENT_KINDS.map((k) => ({
                value: k,
                label: DOCUMENT_KIND_LABEL[k],
              }))}
            />
          </div>
          {/* Строк в документе сервер не хранит: сумма, НДС, валюта, клиент и
              юрлицо копируются из заказа, а позиции подтягиваются при печати
              (контракт сервера 2026-09-21). Обещать «состав документа» нельзя. */}
          <p className="text-xs text-on-surface-variant">
            Создадим черновик счёта на сумму заказа; позиции подтянутся из
            заказа при печати. Реквизиты подставятся из организации клиента,
            выпуск и PDF — на странице документа.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="min-h-12 sm:min-h-10"
            onClick={onClose}
            disabled={saving}
          >
            Отмена
          </Button>
          <Button
            className="min-h-12 sm:min-h-10"
            onClick={() => void onSubmit()}
            disabled={saving}
          >
            {saving ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            Создать черновик
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
